import { Controller } from "@hotwired/stimulus"

// Poll a status turbo-frame until data-poll-continue is gone, then do one
// Turbo.visit to refresh snapshot/trends/upload and stop.
//
// Uses fetch + replaceWith for polling (never frame.src) so a missing frame
// in the response cannot break out into a full-page reload every 2s.
export default class extends Controller {
  static values = {
    interval: { type: Number, default: 2000 },
    frame: { type: String, default: "" },
    url: { type: String, default: "" }
  }

  connect() {
    this.finished = false
    this.inFlight = false
    this.timer = setInterval(() => this.reload(), this.intervalValue)
  }

  disconnect() {
    this.stopPolling()
  }

  async reload() {
    if (this.finished || this.inFlight) return

    const frameId = this.frameValue
    if (!frameId) return

    this.inFlight = true
    try {
      const html = await this.fetchFrame(frameId)
      const next = this.parseFrame(html, frameId)
      if (!next) return

      const keepGoing = next.dataset.pollContinue === "true"
      const current = document.getElementById(frameId)
      if (current) current.replaceWith(next)

      if (!keepGoing) this.finish()
    } catch (_) {
      // Keep polling; transient network errors should not stop the job UI.
    } finally {
      this.inFlight = false
    }
  }

  finish() {
    if (this.finished) return
    this.finished = true
    this.stopPolling()
    this.teardown()

    // One navigation syncs status, upload, snapshot, and trends; new page has
    // no poll-frame, so the 2s loop cannot continue.
    if (window.Turbo) {
      window.Turbo.visit(this.pollUrl, { action: "replace" })
    } else {
      window.location.href = this.pollUrl
    }
  }

  stopPolling() {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  teardown() {
    ;[...this.element.attributes].forEach((attr) => {
      if (attr.name.startsWith("data-poll-frame-")) {
        this.element.removeAttribute(attr.name)
      }
    })

    const controllers = (this.element.getAttribute("data-controller") || "")
      .split(/\s+/)
      .filter((name) => name && name !== "poll-frame")

    if (controllers.length > 0) {
      this.element.setAttribute("data-controller", controllers.join(" "))
    } else {
      this.element.removeAttribute("data-controller")
    }
  }

  async fetchFrame(frameId) {
    const response = await fetch(this.pollUrl, {
      headers: {
        Accept: "text/html",
        "Turbo-Frame": frameId,
        "X-Requested-With": "XMLHttpRequest"
      },
      credentials: "same-origin"
    })
    if (!response.ok) throw new Error(`poll failed: ${response.status}`)
    return response.text()
  }

  parseFrame(html, frameId) {
    const doc = new DOMParser().parseFromString(html, "text/html")
    return doc.getElementById(frameId)
  }

  get pollUrl() {
    return this.urlValue || window.location.href
  }
}
